import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';
import { 
  MdSearch, 
  MdClose, 
  MdRefresh,
  MdBusinessCenter,
  MdInfo,
  MdChevronRight
} from 'react-icons/md';
import { FaFilePdf, FaFileExcel, FaFilter, FaCalendarAlt, FaUser, FaFileContract, FaBook, FaClock } from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const SLAReportByAgentPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    agent_id: '',
    start_date: '',
    end_date: ''
  });
  
  // Report data
  const [reportData, setReportData] = useState([]);
  
  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Validation popup state
  const [showValidationPopup, setShowValidationPopup] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  // Agentları getir
  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/agents', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Rapor oluştur
  const generateReport = async () => {
    // Tarih seçimi zorunlu
    if (!filters.start_date || !filters.end_date) {
      setShowValidationPopup(true);
      return;
    }

    try {
      setIsLoading(true);
      
      // Seçili agent'ın CLOSED durumundaki case'lerini getir
      const caseParams = { 
        case_status: 'CLOSED',
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      
      if (filters.agent_id && filters.agent_id !== 'all') {
        caseParams.owner_id = filters.agent_id;
      }
      
      const casesResponse = await api.get('/support-cases', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: caseParams
      });
      
      // Her case için SLA istatistiklerini al
      const enrichedData = [];
      for (const caseItem of casesResponse.data) {
        try {
          const slaResponse = await api.get(`/support-cases/${caseItem.support_case_uuid}/sla-statistics`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          if (slaResponse.data) {
            enrichedData.push({
              ...caseItem,
              slaStatistics: slaResponse.data
            });
          }
        } catch (slaError) {
          console.error(`SLA istatistikleri alınırken hata (Case ID: ${caseItem.id}):`, slaError);
        }
      }
      
      setReportData(enrichedData);
      setReportGenerated(true);
      setCurrentPage(1);
      
    } catch (error) {
      console.error('Error generating SLA report by agent:', error);
      alert(t('reports.slaReportByAgentGenerationError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Filtreleri temizle
  const handleClearFilters = () => {
    setFilters({
      agent_id: '',
      start_date: '',
      end_date: ''
    });
    setReportGenerated(false);
    setReportData([]);
    setCurrentPage(1);
  };

  // Excel export
  const exportToExcel = () => {
    if (reportData.length === 0) {
      alert(t('reports.noDataToExport'));
      return;
    }

    // Helper function for clean text
    const cleanTextForExcel = (text) => {
      if (!text) return '';
      return text.toString().replace(/undefined/g, '');
    };

    // Prepare data for Excel
    const excelData = reportData.map((item, index) => ({
      [t('reports.serialNumber')]: index + 1,
      [t('reports.caseId')]: cleanTextForExcel(item.case_id),
      [t('reports.agent')]: cleanTextForExcel(item.created_by_name || '-'),
      [t('reports.companyName')]: cleanTextForExcel(item.customer_name),
      [t('reports.openingDate')]: cleanTextForExcel(item.created_at ? new Date(item.created_at).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : ''),
      [t('reports.closingDate')]: cleanTextForExcel(item.closed_time ? new Date(item.closed_time).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : '-'),
      [t('reports.firstResponseDate')]: cleanTextForExcel(item.slaStatistics?.firstResponseTime ? new Date(item.slaStatistics.firstResponseTime).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : ''),
      [t('reports.resolutionDate')]: cleanTextForExcel(item.slaStatistics?.resolvedTime ? new Date(item.slaStatistics.resolvedTime).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : '-'),
      [t('reports.responseTime')]: cleanTextForExcel(item.slaStatistics?.responseTimeFormatted || ''),
      [t('reports.responseWithinSLA')]: cleanTextForExcel(item.slaStatistics?.isResponseWithinSLA ? t('common.yes') : t('common.no')),
      [t('reports.resolutionTime')]: cleanTextForExcel(item.slaStatistics?.resolvedTime ? item.slaStatistics?.resolutionTimeFormatted : '-'),
      [t('reports.resolutionWithinSLA')]: cleanTextForExcel(item.slaStatistics?.isResolutionWithinSLA ? t('common.yes') : t('common.no'))
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

         // Set column widths
     const colWidths = [
       { wch: 8 },   // Sira No
       { wch: 12 },  // Case ID
       { wch: 25 },  // Agent
       { wch: 30 },  // Müşteri
       { wch: 15 },  // Açılış Tarihi
       { wch: 15 },  // Kapanış Tarihi
       { wch: 15 },  // İlk Yanıt Tarihi
       { wch: 15 },  // Çözüm Tarihi
       { wch: 15 },  // Yanıt Süresi
       { wch: 15 },  // SLA İçinde Yanıt
       { wch: 15 },  // Çözüm Süresi
       { wch: 15 }   // SLA İçinde Çözüm
     ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, t('reports.slaReportByAgent'));

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('reports.slaReportByAgent').replace(/\s+/g, '_')}_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // PDF export  
  const exportToPDF = () => {
    if (reportData.length === 0) {
      alert(t('reports.noDataToExport'));
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

    // Helper function to handle Turkish characters
    const cleanText = (text) => {
      if (!text) return '';
      
      // Convert Turkish characters to Latin equivalents for PDF compatibility
      const turkishToLatin = {
        'ç': 'c', 'Ç': 'C',
        'ğ': 'g', 'Ğ': 'G', 
        'ı': 'i', 'I': 'I',
        'İ': 'I', 'i': 'i',
        'ö': 'o', 'Ö': 'O',
        'ş': 's', 'Ş': 'S',
        'ü': 'u', 'Ü': 'U'
      };
      
      let cleanedText = text.toString().replace(/undefined/g, '');
      
      // Replace Turkish characters
      for (const [turkish, latin] of Object.entries(turkishToLatin)) {
        cleanedText = cleanedText.replace(new RegExp(turkish, 'g'), latin);
      }
      
      return cleanedText;
    };

    // Add title
    doc.setFontSize(16);
    doc.text(t('reports.slaReportByAgent'), 14, 22);

    // Add generation date
    doc.setFontSize(10);
    doc.text(`${t('reports.reportDate')}: ${new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')}`, 14, 30);

    // Filter info
    if (filters.agent_id && filters.agent_id !== 'all') {
      const selectedAgent = agents.find(a => a.id == filters.agent_id);
      doc.text(`${t('reports.agent')}: ${selectedAgent ? cleanText(selectedAgent.full_name) : filters.agent_id}`, 14, 38);
    }
    if (filters.start_date && filters.end_date) {
      doc.text(`${t('reports.dateRange')}: ${filters.start_date} - ${filters.end_date}`, 14, 46);
    }
    
    // Prepare table data with clean text
    const tableData = reportData.map((item, index) => [
      index + 1,
             cleanText(item.case_id || ''),
       cleanText(((item.created_by_name || '-') || '').substring(0, 20) + ((item.created_by_name || '-')?.length > 20 ? '...' : '')),
       cleanText((item.customer_name || '').substring(0, 20) + (item.customer_name?.length > 20 ? '...' : '')),
             cleanText(item.created_at ? new Date(item.created_at).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : ''),
       cleanText(item.closed_time ? new Date(item.closed_time).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : '-'),
       cleanText(item.slaStatistics?.firstResponseTime ? new Date(item.slaStatistics.firstResponseTime).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : ''),
       cleanText(item.slaStatistics?.resolvedTime ? new Date(item.slaStatistics.resolvedTime).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : '-'),
      cleanText(item.slaStatistics?.responseTimeFormatted || ''),
      cleanText(item.slaStatistics?.isResponseWithinSLA ? t('common.yes') : t('common.no')),
             cleanText(item.slaStatistics?.resolvedTime ? item.slaStatistics?.resolutionTimeFormatted : '-'),
      cleanText(item.slaStatistics?.isResolutionWithinSLA ? t('common.yes') : t('common.no'))
    ]);

         // Add table
     autoTable(doc, {
       head: [['#', t('reports.caseId'), t('reports.agent'), t('reports.companyName'), t('reports.openingDate'), t('reports.closingDate'), t('reports.firstResponseDate'), t('reports.resolutionDate'), t('reports.responseTime'), t('reports.responseWithinSLA'), t('reports.resolutionTime'), t('reports.resolutionWithinSLA')]],
      body: tableData,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] }
    });

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('reports.slaReportByAgent').replace(/\s+/g, '_')}_${currentDate}.pdf`;

    doc.save(filename);
  };

  // Pagination logic
  const pageSizeOptions = [10, 20, 50, 100];
  const totalPages = Math.ceil(reportData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = reportData.slice(indexOfFirstItem, indexOfLastItem);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değiştiğinde ilk sayfaya dön
  };

  return (
    <div className="h-full overflow-auto">
      {/* Breadcrumb */}
      <div className="flex justify-between items-center mb-6">
        <nav className="flex items-center text-sm">
          <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
            <span className="text-[#fff] font-medium">
              {t('common.reports')}
            </span>
            <MdChevronRight className="mx-2 text-[#fff]" size={20} />
            <span className="text-[#fff] font-medium">
              {t('reports.slaReportByAgent')}
            </span>
          </div>
        </nav>
      </div>

      {/* Filter Panel */}
      <div className="dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 mb-6">
        {/* Filter Panel Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
          <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('reports.agentSlaInfo')}</h1>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Agent Seçimi */}
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reports.agentSelection')}
              </label>
              <select
                value={filters.agent_id}
                onChange={(e) => setFilters(prev => ({ ...prev, agent_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('reports.selectAgent')}</option>
                <option value="all">{t('reports.all')}</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.full_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Başlangıç Tarihi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reports.startDate')}
              </label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-calendar-picker-indicator]:dark:invert"
                required
              />
            </div>

            {/* Bitiş Tarihi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reports.endDate')}
              </label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-calendar-picker-indicator]:dark:invert"
                required
              />
            </div>
          </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4">
              <button
                onClick={generateReport}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-[#232b3b] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50"
              >
                <MdSearch size={16} />
                {isLoading ? t('reports.generating') : t('common.show')}
              </button>

              <button
                onClick={handleClearFilters}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                <MdClose size={16} />
                {t('reports.clearFilters')}
              </button>

              <button
                onClick={exportToExcel}
                disabled={!reportGenerated || reportData.length === 0}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaFileExcel size={16} />
                {t('reports.downloadExcel')}
              </button>

              <button
                onClick={exportToPDF}
                disabled={!reportGenerated || reportData.length === 0}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaFilePdf size={16} />
                {t('reports.downloadPDF')}
              </button>
            </div>
        </div>
      </div>

      {/* Report Results */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
          <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('reports.report')}</h1>
        </div>
        
        {reportGenerated ? (
          <>
            {/* Sayfa boyutu seçici */}
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('common.show')}</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('table.records')}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {t('table.totalRecordsFound', { count: reportData.length })}
                </span>
              </div>
            </div>

            {reportData.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.caseId')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.agent')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.companyName')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.openingDate')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.closingDate')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.firstResponseDate')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.resolutionDate')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.responseTime')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.responseWithinSLA')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.resolutionTime')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.resolutionWithinSLA')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentData.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {item.case_id}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {item.created_by_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {item.customer_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {item.created_at ? new Date(item.created_at).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {item.closed_time ? new Date(item.closed_time).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {item.slaStatistics?.firstResponseTime ? new Date(item.slaStatistics.firstResponseTime).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {item.slaStatistics?.resolvedTime ? new Date(item.slaStatistics.resolvedTime).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {item.slaStatistics?.responseTimeFormatted || ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.slaStatistics?.isResponseWithinSLA 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {item.slaStatistics?.isResponseWithinSLA ? t('common.yes') : t('common.no')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {item.slaStatistics?.resolvedTime ? item.slaStatistics?.resolutionTimeFormatted : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.slaStatistics?.isResolutionWithinSLA 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {item.slaStatistics?.isResolutionWithinSLA ? t('common.yes') : t('common.no')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3 p-4">
                  {currentData.map((item, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                      <div className="grid grid-cols-1 gap-3">
                        {/* Case ID and Agent Row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.caseId')}:
                            </span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white text-right">
                              {item.case_id}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.agent')}:
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300 text-right">
                              {item.created_by_name || '-'}
                            </span>
                          </div>
                        </div>

                        {/* Company Name */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                            {t('reports.companyName')}:
                          </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300 text-right">
                            {item.customer_name}
                          </span>
                        </div>

                        {/* Opening and Closing Date Row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.openingDate')}:
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white text-right">
                              {item.created_at ? new Date(item.created_at).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : ''}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.closingDate')}:
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white text-right">
                              {item.closed_time ? new Date(item.closed_time).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : '-'}
                            </span>
                          </div>
                        </div>

                        {/* Response and Resolution Date Row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.firstResponseDate')}:
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white text-right">
                              {item.slaStatistics?.firstResponseTime ? new Date(item.slaStatistics.firstResponseTime).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : ''}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.resolutionDate')}:
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white text-right">
                              {item.slaStatistics?.resolvedTime ? new Date(item.slaStatistics.resolvedTime).toLocaleString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR') : '-'}
                            </span>
                          </div>
                        </div>

                        {/* Response Time and Within SLA Row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.responseTime')}:
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white text-right">
                              {item.slaStatistics?.responseTimeFormatted || ''}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.responseWithinSLA')}:
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              item.slaStatistics?.isResponseWithinSLA 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {item.slaStatistics?.isResponseWithinSLA ? t('common.yes') : t('common.no')}
                            </span>
                          </div>
                        </div>

                        {/* Resolution Time and Within SLA Row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.resolutionTime')}:
                            </span>
                            <span className="text-sm text-gray-900 dark:text-white text-right">
                              {item.slaStatistics?.resolvedTime ? item.slaStatistics?.resolutionTimeFormatted : '-'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                              {t('reports.resolutionWithinSLA')}:
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              item.slaStatistics?.isResolutionWithinSLA 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {item.slaStatistics?.isResolutionWithinSLA ? t('common.yes') : t('common.no')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 bg-white dark:bg-gray-600 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-500"
                        >
                          {t('table.previous')}
                        </button>
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {t('table.page')} {currentPage} / {totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 bg-white dark:bg-gray-600 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-500"
                        >
                          {t('table.next')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="px-6 py-8 text-center">
                <p className="text-gray-500 dark:text-gray-400">{t('reports.noDataFound')}</p>
              </div>
            )}
          </>
        ) : (
          <div className="px-6 py-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">{t('reports.useFiltersToGenerate')}</p>
          </div>
        )}
      </div>

      {/* Validation Popup */}
      {showValidationPopup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('common.warning')}</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 dark:text-gray-300">
                {t('reports.dateRangeRequired')}
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowValidationPopup(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
              >
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SLAReportByAgentPage;


